/** PATRON PAGES — the one renderer.
 *
 *  The fan's page and the editor's preview both draw THIS component, so the
 *  preview cannot drift from what a phone shows. It takes a resolved payload
 *  from `/api/patron/public/{card}/page/{page}` (or the same shape built from
 *  the editor's unsaved draft) and nothing else: every number in the text has
 *  already been replaced by the live value.
 *
 *  Mobile first: one column, 44px tap targets, nothing wider than the screen.
 *  On an iPad the column is centred, never stretched.
 */
import { useEffect } from 'react';
import { bodyPx, fontStack, headlinePx, loadFont } from '../../lib/designTokens';

const API_BASE = (typeof window !== 'undefined' ? window.location.origin : '');
export const absUrl = (u) => (!u ? null : (String(u).startsWith('http') ? u : `${API_BASE}${u}`));

const PICTURE_PX = { S: 64, M: 88, L: 120 };

const Row = ({ block, design }) => {
    const px = PICTURE_PX[block.picture_size] || PICTURE_PX.M;
    const picture = absUrl(block.picture_url);
    // "title" is the default for every row, including rows saved before this
    // setting existed: the picture sits beside the title and the points line,
    // and the body runs the full width underneath instead of being squeezed
    // into a strip beside a tall picture.
    const pos = picture ? (block.picture_pos || 'title') : 'all';

    const titleEl = block.title ? (
        <p className="font-bold leading-snug break-words"
           style={{ fontFamily: fontStack(design.headline_font) || undefined, fontSize: 18 }}
           data-testid={`patron-page-row-title-${block.id}`}>{block.title}</p>
    ) : null;
    const pointsEl = block.points ? (
        <p className="font-bold leading-snug break-words mt-0.5"
           style={{ color: design.accent, fontSize: 18 }}
           data-testid={`patron-page-row-points-${block.id}`}>{block.points}</p>
    ) : null;
    const bodyEl = block.body ? (
        <p className="opacity-90 leading-relaxed break-words"
           style={{ fontFamily: fontStack(design.body_font) || undefined,
                    fontSize: bodyPx(design.body_size) || 16 }}
           data-testid={`patron-page-row-body-${block.id}`}>{block.body}</p>
    ) : null;
    const pictureEl = picture ? (
        <img src={picture} alt="" loading="lazy"
             style={{ width: px, height: px }}
             className="rounded-xl object-cover shrink-0"
             data-testid={`patron-page-row-picture-${block.id}`} />
    ) : null;

    return (
        <div className="py-4 border-b last:border-b-0"
             style={{ borderColor: 'rgba(255,255,255,0.12)' }}
             data-testid={`patron-page-row-${block.id}`}
             data-picture-pos={pos}>
            {pos === 'all' ? (
                <div className="flex gap-3">
                    {pictureEl}
                    <div className="min-w-0 flex-1">
                        {titleEl}{pointsEl}
                        {bodyEl && <div className="mt-1">{bodyEl}</div>}
                    </div>
                </div>
            ) : pos === 'above' ? (
                <div className="min-w-0">
                    {pictureEl && <div className="mb-2">{pictureEl}</div>}
                    {titleEl}{pointsEl}
                    {bodyEl && <div className="mt-1">{bodyEl}</div>}
                </div>
            ) : (
                <div className="min-w-0">
                    <div className="flex gap-3 items-start">
                        {pictureEl}
                        <div className="min-w-0 flex-1">{titleEl}{pointsEl}</div>
                    </div>
                    {bodyEl && <div className="mt-2">{bodyEl}</div>}
                </div>
            )}
        </div>
    );
};

const BigNumber = ({ block, design }) => (
    <div className="py-5 text-center" data-testid={`patron-page-number-${block.id}`}>
        <p className="font-bold tabular-nums leading-none break-words"
           style={{ color: design.accent,
                    fontFamily: fontStack(design.headline_font) || undefined,
                    fontSize: headlinePx(design.headline_size) || 36 }}
           data-testid={`patron-page-number-value-${block.id}`}>{block.value}</p>
        {block.label && (
            <p className="mt-1 text-sm opacity-80"
               data-testid={`patron-page-number-label-${block.id}`}>{block.label}</p>
        )}
    </div>
);

const ActionButton = ({ block, design, onAction }) => (
    <button type="button" onClick={() => onAction?.(block.action)}
            data-testid={`patron-page-button-${block.id}`}
            className="w-full min-h-[44px] rounded-xl font-bold px-4 my-3"
            style={{ backgroundColor: design.button, color: design.bg }}>
        {block.label}
    </button>
);

export const PatronPageView = ({ page, onAction, framed = false }) => {
    const design = page?.design || {};
    const branding = page?.branding || {};

    useEffect(() => {
        loadFont(design.headline_font);
        loadFont(design.body_font);
    }, [design.headline_font, design.body_font]);

    if (!page) return null;
    const wallpaper = absUrl(branding.wallpaper_url);
    // Fit, zoom and position belong to the wallpaper alone. The logo sliders
    // only set their own image's height — but they change how TALL this page
    // is, and a "fill the screen" background is re-cropped whenever its box
    // changes shape, which is why the wallpaper used to appear to move when a
    // logo grew. These controls let a venue pin it where they want.
    const zoom = Math.max(100, Math.min(200, Number(branding.wallpaper_zoom) || 100));
    const posX = Number.isFinite(Number(branding.wallpaper_x)) ? Number(branding.wallpaper_x) : 50;
    const posY = Number.isFinite(Number(branding.wallpaper_y)) ? Number(branding.wallpaper_y) : 50;

    return (
        <div className={`${framed ? 'relative' : 'min-h-screen'} w-full overflow-hidden`}
             style={{ backgroundColor: design.bg, color: design.text }}
             data-testid="patron-page">
            {wallpaper && (
                <>
                    <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
                        <div className="absolute inset-0 bg-no-repeat"
                             style={{ backgroundImage: `url(${wallpaper})`,
                                      backgroundSize: branding.wallpaper_fit === 'whole' ? 'contain' : 'cover',
                                      backgroundPosition: `${posX}% ${posY}%`,
                                      transform: zoom === 100 ? undefined : `scale(${zoom / 100})`,
                                      transformOrigin: `${posX}% ${posY}%` }}
                             data-testid="patron-page-wallpaper"
                             data-fit={branding.wallpaper_fit || 'fill'}
                             data-zoom={zoom} />
                    </div>
                    <div className="absolute inset-0"
                         style={{ backgroundColor: `rgba(0,0,0,${(Number(branding.wallpaper_dark) || 0) / 100})` }}
                         aria-hidden="true" data-testid="patron-page-wallpaper-dark" />
                </>
            )}

            <div className="relative mx-auto w-full max-w-sm px-5 pt-8 pb-14">
                {(branding.merchant_logo_url || branding.program_logo_url) && (
                    <div className="flex flex-col items-center gap-2 mb-5">
                        {branding.merchant_logo_url && (
                            <img src={absUrl(branding.merchant_logo_url)} alt=""
                                 style={{ maxWidth: '100%', height: Number(branding.merchant_logo_px) || 96 }}
                                 className="object-contain"
                                 data-testid="patron-page-merchant-logo" />
                        )}
                        {branding.program_logo_url && (
                            <img src={absUrl(branding.program_logo_url)} alt=""
                                 style={{ maxWidth: '100%', height: Number(branding.program_logo_px) || 72 }}
                                 className="object-contain"
                                 data-testid="patron-page-program-logo" />
                        )}
                    </div>
                )}

                {page.title && (
                    <h1 className="font-bold leading-tight mb-4 break-words"
                        style={{ fontFamily: fontStack(design.headline_font) || undefined,
                                 fontSize: headlinePx(design.headline_size) || 36 }}
                        data-testid="patron-page-title">{page.title}</h1>
                )}

                <div className="rounded-2xl px-4"
                     style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
                     data-testid="patron-page-blocks">
                    {(page.blocks || []).map((b) => {
                        if (b.type === 'row') return <Row key={b.id} block={b} design={design} />;
                        if (b.type === 'number') return <BigNumber key={b.id} block={b} design={design} />;
                        if (b.type === 'button') {
                            return <ActionButton key={b.id} block={b} design={design} onAction={onAction} />;
                        }
                        return null;
                    })}
                </div>
            </div>
        </div>
    );
};

export default PatronPageView;
